import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { AmlPepEntity, AmlPepStatus, LegalCase, LegalGroup, ReportData } from '../tokens/types';
import { PdfSection, PdfDataTable, PdfKpiGrid, PdfDivider } from '../primitives/PdfPrimitives';
import { ScreenSection, ScreenDataTable, ScreenKpiGrid, ScreenDivider } from '../primitives/ScreenPrimitives';
import { colors, coverFonts, fontFamily, overviewColors, ptToPx, space, typeScale } from '../tokens/designTokens';
import { fmtDate } from './formatUtils';
import { PdfInlineInsights, ScreenInlineInsights } from './InsightsInline';

const flatten = (groups: LegalGroup[]): LegalCase[] => groups.flatMap((g) => g.cases);

const truncateParty = (s: string, max = 90): string => {
    if (!s) return '—';
    const cleaned = s.trim();
    if (cleaned.length <= max) return cleaned;
    return cleaned.slice(0, max).trimEnd() + '…';
};

// Per PM feedback: render full case strings, no truncation. PdfDataTable
// cells use `wrap` and rows use `wrap={false}`, so cells reflow to fit and
// the row grows to the tallest cell. The `truncateParty` helper still
// exists in this file for any local emergency reuse but is not called.
const toRows = (cases: LegalCase[]) =>
    cases.slice(0, 20).map((c) => ({
        caseNum: c.caseNum || '—',
        party: c.filedBy ? `${c.filedBy} → ${c.filedAgainst}` : c.filedAgainst || '—',
        category: c.category || '—',
        court: c.court || '—',
        status: c.status,
        filed: fmtDate(c.filingDate),
    }));

// Column widths sum to 100% across body width (~526pt at the new tightened
// page padding). Cells wrap if their content is long; row height grows.
const columns = [
    { key: 'caseNum', header: 'Case #', width: '18%' },
    { key: 'party', header: 'Parties', width: '30%' },
    { key: 'category', header: 'Category', width: '16%' },
    { key: 'court', header: 'Court', width: '14%' },
    { key: 'status', header: 'Status', width: '11%' },
    { key: 'filed', header: 'Filed', width: '11%' },
];

// ──────────────────────────────────────────────────────────────────────
// Regulatory Checks matrix (Figma 32:102215)
// ──────────────────────────────────────────────────────────────────────

const PF = coverFonts.pdf;
const SF = coverFonts.screen;

const matrixDesign = {
    gridGap: 11.34,
    // Trimmed cardPad 12.34 → 10 to give the title row more horizontal slack;
    // long entity names ("INCUSPAZE SOLUTIONS PRIVATE LIMITED") were clipping
    // the card right edge at the previous padding.
    cardPad: 10,
    cardRadius: 4,
    cardBorder: 0.5,
    cardBorderColor: overviewColors.border,
    cardBg: '#FFFFFF',

    // Title typography. The contract:
    //  - source text bound at the normalizer (28 chars max, see normalizers.ts)
    //  - rendered at 7.5pt uppercase with `flex: 1` so react-pdf measures
    //    against the card width and forces wrap when the line wouldn't fit
    //  - title row reserves a fixed 2-line minHeight so cards in a row stay
    //    equal-height regardless of whether names wrap (skill rule 3)
    //  - max characters × fontSize at this scale is guaranteed ≤ 2 lines in
    //    the ~237pt card content area, so overflow is structurally impossible
    titleSize: 7.5,
    titleTrackingEm: 0,
    titleLineHeight: 1.3,
    titleMaxLines: 2,
    titleColor: overviewColors.ink,
    titleGap: 8,

    // Settled on 5pt after balancing legibility vs page-fit. 6.5pt looked
    // airier but pushed a 9-dimension card past 270pt and caused page-bottom
    // overflow on the row that landed near a page break. 5pt keeps each row
    // visibly distinct and the worst-case card under ~250pt.
    rowDividerColor: '#F0EFE9',
    rowPadY: 5,
    rowLabelSize: 9,
    rowLabelColor: overviewColors.inkSoft,
    dotSize: 7,

    statusBg: {
        clear: '#059669',
        watch: '#D97706',
        hit: '#DC2626',
        unavailable: '#9CA3AF',
    } as Record<AmlPepStatus, string>,
} as const;

// Pair entities into 2-column rows (skill rule 2 — explicit rows, never flexWrap).
const pairsOf = <T,>(arr: T[]): Array<[T | undefined, T | undefined]> => {
    const out: Array<[T | undefined, T | undefined]> = [];
    for (let i = 0; i < arr.length; i += 2) out.push([arr[i], arr[i + 1]]);
    return out;
};

const PdfRegulatoryCard: React.FC<{ entity: AmlPepEntity }> = ({ entity }) => {
    return (
        <View
            wrap={false}
            style={{
                borderWidth: matrixDesign.cardBorder,
                borderColor: matrixDesign.cardBorderColor,
                borderRadius: matrixDesign.cardRadius,
                backgroundColor: matrixDesign.cardBg,
                padding: matrixDesign.cardPad,
            }}
        >
            {/* Title row reserves a 2-line minHeight (skill rule 3) so cards
                in the same row stay equal-height whether names wrap or not.
                `flex: 1` on the Text (skill rule 4) forces react-pdf to
                measure against the card width and wrap when it can't fit. */}
            <View
                style={{
                    flexDirection: 'row',
                    minHeight: matrixDesign.titleSize * matrixDesign.titleLineHeight * matrixDesign.titleMaxLines,
                    marginBottom: matrixDesign.titleGap,
                }}
            >
                <Text
                    style={{
                        flex: 1,
                        fontSize: matrixDesign.titleSize,
                        fontFamily: PF.sans,
                        fontWeight: 600,
                        color: matrixDesign.titleColor,
                        letterSpacing: matrixDesign.titleSize * matrixDesign.titleTrackingEm,
                        lineHeight: matrixDesign.titleLineHeight,
                        textTransform: 'uppercase',
                    }}
                >
                    {entity.name}
                </Text>
            </View>
            {entity.dimensions.map((dim, i) => {
                const isLast = i === entity.dimensions.length - 1;
                return (
                    <View
                        key={dim.label}
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 6,
                            paddingTop: matrixDesign.rowPadY,
                            paddingBottom: matrixDesign.rowPadY,
                            borderBottomWidth: isLast ? 0 : matrixDesign.cardBorder,
                            borderBottomColor: matrixDesign.rowDividerColor,
                        }}
                    >
                        <View
                            style={{
                                width: matrixDesign.dotSize,
                                height: matrixDesign.dotSize,
                                borderRadius: matrixDesign.dotSize / 2,
                                backgroundColor: matrixDesign.statusBg[dim.status],
                                flexShrink: 0,
                            }}
                        />
                        {/* Per AicaReportV2 skill rule 4: put `flex: 1` directly on
                            the Text (not a wrapper View) so react-pdf wraps
                            it cleanly when a dimension label is long. */}
                        <Text
                            style={{ flex: 1, fontSize: matrixDesign.rowLabelSize, fontFamily: PF.sans, color: matrixDesign.rowLabelColor }}
                        >
                            {dim.label}
                        </Text>
                    </View>
                );
            })}
        </View>
    );
};

const PdfRegulatoryMatrix: React.FC<{ entities: AmlPepEntity[] }> = ({ entities }) => {
    return (
        <View style={{ flexDirection: 'column', gap: matrixDesign.gridGap }}>
            {pairsOf(entities).map((row, ri) => (
                // Per AicaReportV2 skill rule 1: never use `wrap={false}` for spacing.
                // The cards inside are individually `wrap={false}` so a single card
                // never splits mid-content; the row itself is free to move to the
                // next page when the current page can't fit a worst-case card.
                // 280pt = max card height (5pt rowPadY × 9 dimensions ≈ 250pt) +
                // safety margin so the row reliably moves to the next page rather
                // than placing here and overflowing into the page footer.
                <View key={ri} style={{ flexDirection: 'row', gap: matrixDesign.gridGap }} minPresenceAhead={280}>
                    {row.map((entity, ci) =>
                        entity ? (
                            <View key={ci} style={{ flex: 1 }}>
                                <PdfRegulatoryCard entity={entity} />
                            </View>
                        ) : (
                            <View key={ci} style={{ flex: 1 }} />
                        ),
                    )}
                </View>
            ))}
        </View>
    );
};

const ScreenRegulatoryCard: React.FC<{ entity: AmlPepEntity }> = ({ entity }) => {
    return (
        <div
            style={{
                border: `${ptToPx(matrixDesign.cardBorder)}px solid ${matrixDesign.cardBorderColor}`,
                borderRadius: ptToPx(matrixDesign.cardRadius),
                background: matrixDesign.cardBg,
                padding: ptToPx(matrixDesign.cardPad),
            }}
        >
            <div
                style={{
                    fontSize: ptToPx(matrixDesign.titleSize),
                    fontFamily: SF.sans,
                    fontWeight: 600,
                    color: matrixDesign.titleColor,
                    letterSpacing: `${matrixDesign.titleTrackingEm}em`,
                    lineHeight: matrixDesign.titleLineHeight,
                    textTransform: 'uppercase',
                    marginBottom: ptToPx(matrixDesign.titleGap),
                    minHeight: ptToPx(matrixDesign.titleSize * matrixDesign.titleLineHeight * matrixDesign.titleMaxLines),
                    wordBreak: 'break-word',
                }}
            >
                {entity.name}
            </div>
            {entity.dimensions.map((dim, i) => {
                const isLast = i === entity.dimensions.length - 1;
                return (
                    <div
                        key={dim.label}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: ptToPx(6),
                            paddingTop: ptToPx(matrixDesign.rowPadY),
                            paddingBottom: ptToPx(matrixDesign.rowPadY),
                            borderBottom: isLast ? 'none' : `${ptToPx(matrixDesign.cardBorder)}px solid ${matrixDesign.rowDividerColor}`,
                        }}
                    >
                        <span
                            style={{
                                display: 'inline-block',
                                flexShrink: 0,
                                width: ptToPx(matrixDesign.dotSize),
                                height: ptToPx(matrixDesign.dotSize),
                                borderRadius: ptToPx(matrixDesign.dotSize / 2),
                                background: matrixDesign.statusBg[dim.status],
                            }}
                        />
                        <span
                            style={{ fontSize: ptToPx(matrixDesign.rowLabelSize), fontFamily: SF.sans, color: matrixDesign.rowLabelColor }}
                        >
                            {dim.label}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

const ScreenRegulatoryMatrix: React.FC<{ entities: AmlPepEntity[] }> = ({ entities }) => {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: ptToPx(matrixDesign.gridGap) }}>
            {pairsOf(entities).map((row, ri) => (
                <div key={ri} style={{ display: 'flex', gap: ptToPx(matrixDesign.gridGap) }}>
                    {row.map((entity, ci) =>
                        entity ? (
                            <div key={ci} style={{ flex: 1, minWidth: 0 }}>
                                <ScreenRegulatoryCard entity={entity} />
                            </div>
                        ) : (
                            <div key={ci} style={{ flex: 1 }} />
                        ),
                    )}
                </div>
            ))}
        </div>
    );
};

// Sub-section heading reused for "Regulatory Checks" / "Legal Cases".
const PdfH3: React.FC<{ children: React.ReactNode; subtitle?: string; topGap?: number }> = ({ children, subtitle, topGap = space.md }) => (
    <View style={{ marginTop: topGap }}>
        <Text style={{ fontSize: typeScale.body, fontFamily: fontFamily.sansBold, color: colors.ink }}>{children}</Text>
        {subtitle ? <Text style={{ fontSize: typeScale.micro, color: colors.muted, marginTop: 2 }}>{subtitle}</Text> : null}
    </View>
);

const ScreenH3: React.FC<{ children: React.ReactNode; subtitle?: string; topGap?: number }> = ({ children, subtitle, topGap = 16 }) => (
    <div style={{ marginTop: topGap }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: colors.ink }}>{children}</div>
        {subtitle ? <div style={{ fontSize: 11, color: colors.muted, marginTop: 2 }}>{subtitle}</div> : null}
    </div>
);

const PdfLegalRisk: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const lr = data.legalRisk;
    if (!lr) return null;
    const against = flatten(lr.casesAgainst);
    const by = flatten(lr.casesBy);
    return (
        <PdfSection kicker="Risk" title="Regulatory & Legal" index={index} breakBefore={breakBefore}>
            <PdfKpiGrid
                items={[
                    { label: 'Cases against', value: String(against.length) },
                    { label: 'Cases filed by', value: String(by.length) },
                    { label: 'AML / PEP hits', value: String(lr.amlPepCount) },
                    { label: 'Last refreshed', value: fmtDate(lr.lastFetchedDate) },
                ]}
            />

            {lr.amlPepEntities && lr.amlPepEntities.length > 0 ? (
                <View style={{ marginTop: space.md }} minPresenceAhead={160}>
                    <PdfH3 subtitle="AML, PEP and watchlist screening per entity" topGap={0}>
                        Regulatory Checks
                    </PdfH3>
                    <View style={{ marginTop: 8 }}>
                        <PdfRegulatoryMatrix entities={lr.amlPepEntities} />
                    </View>
                </View>
            ) : null}

            {against.length || by.length ? <PdfDivider /> : null}

            {against.length ? (
                <View minPresenceAhead={90}>
                    {/* minPresenceAhead keeps the heading on the same page as
                        at least one row of the table that follows. Pure
                        additive — the heading + first row pair stays together
                        and the rest of the table flows naturally. */}
                    <PdfH3 subtitle="Cases filed against the company or its directors" topGap={0}>
                        Cases against
                    </PdfH3>
                    <View style={{ marginTop: 6 }}>
                        <PdfDataTable columns={columns} rows={toRows(against)} />
                    </View>
                    {against.length > 20 ? (
                        <Text style={{ fontSize: typeScale.micro, color: colors.mutedSoft, marginTop: 4 }}>
                            +{against.length - 20} additional cases omitted.
                        </Text>
                    ) : null}
                </View>
            ) : null}
            {by.length ? (
                <View minPresenceAhead={90}>
                    <PdfH3 subtitle="Cases filed by the company or its directors">Cases filed by</PdfH3>
                    <View style={{ marginTop: 6 }}>
                        <PdfDataTable columns={columns} rows={toRows(by)} />
                    </View>
                </View>
            ) : null}
            <PdfInlineInsights data={data} k="legalChecks" />
        </PdfSection>
    );
};

const ScreenLegalRisk: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const lr = data.legalRisk;
    if (!lr) return null;
    const against = flatten(lr.casesAgainst);
    const by = flatten(lr.casesBy);
    return (
        <ScreenSection kicker="Risk" title="Regulatory & Legal" index={index}>
            <ScreenKpiGrid
                items={[
                    { label: 'Cases against', value: String(against.length) },
                    { label: 'Cases filed by', value: String(by.length) },
                    { label: 'AML / PEP hits', value: String(lr.amlPepCount) },
                    { label: 'Last refreshed', value: fmtDate(lr.lastFetchedDate) },
                ]}
            />
            {lr.amlPepEntities && lr.amlPepEntities.length > 0 ? (
                <div style={{ marginTop: 16 }}>
                    <ScreenH3 subtitle="AML, PEP and watchlist screening per entity" topGap={0}>
                        Regulatory Checks
                    </ScreenH3>
                    <div style={{ marginTop: 12 }}>
                        <ScreenRegulatoryMatrix entities={lr.amlPepEntities} />
                    </div>
                </div>
            ) : null}
            {against.length || by.length ? <ScreenDivider /> : null}
            {against.length ? (
                <>
                    <ScreenH3 subtitle="Cases filed against the company or its directors" topGap={0}>
                        Cases against
                    </ScreenH3>
                    <div style={{ marginTop: 8 }}>
                        <ScreenDataTable columns={columns} rows={toRows(against)} />
                    </div>
                </>
            ) : null}
            {by.length ? (
                <>
                    <ScreenH3 subtitle="Cases filed by the company or its directors">Cases filed by</ScreenH3>
                    <div style={{ marginTop: 8 }}>
                        <ScreenDataTable columns={columns} rows={toRows(by)} />
                    </div>
                </>
            ) : null}
            <ScreenInlineInsights data={data} k="legalChecks" />
        </ScreenSection>
    );
};

export const legalRiskModule: ModuleDefinition = {
    meta: {
        id: 'legalRisk',
        title: 'Regulatory & Legal',
        description: 'Compliance checks, litigation exposure and AML/PEP screening per entity.',
        dataSource: 'Legal Cases + AML/PEP',
    },
    hasData: (d) => {
        const lr = d.legalRisk;
        if (!lr) return false;
        return (
            lr.casesAgainst.some((g) => g.cases.length > 0) ||
            lr.casesBy.some((g) => g.cases.length > 0) ||
            lr.amlPepCount > 0 ||
            (lr.amlPepEntities ?? []).length > 0
        );
    },
    Pdf: PdfLegalRisk,
    Screen: ScreenLegalRisk,
};
